import { useI18n } from 'vue-i18n';

import { Paginator } from 'array-paginator';
import { defineStore } from 'pinia';
import { ResolveListForExportCallback } from 'src/stores/_infrastructure/ResolveListForExportCallback';
import { resolveDesktopModuleDateFilterForQuery } from 'src/stores/_infrastructure/dateTimeRangeQueryValue';
import { dedupByAggregateId } from 'src/stores/_infrastructure/dedupByAggregateId';
import {
	COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
	prepareQueryForCompleteListExport,
} from 'src/stores/_infrastructure/prepareQueryForCompleteListExport';
import {
	FILTER_LIST_GENERIC_ACTIVITY_LIST_SORT,
	FILTER_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE,
} from 'src/stores/filterList/localStorageKeys';
import { GetAllGlobalFilteredItemsQuery } from 'src/stores/filterList/queries';
import createFilterListState from 'src/stores/filterList/state';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { GetAllUsersWithAccessToTheModuleForTheUserFilterQuery } from 'src/stores/genericActivityList/queries';
import { TSMap } from 'typescript-map';

import { DateRangeSelection } from 'src/models/common';
import { FilterBoxes, FilterModule, FilterUsers, FilterUsersRunType } from 'src/models/filter';
import { FilterListItem, FilterListItemRunType } from 'src/models/filterList';
import {
	ActivityListTypeEnum,
	GenericActivityListPagination,
	GenericActivityListSort,
} from 'src/models/genericActivityList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { SerializedQuery } from 'src/models/query';
import { StatusEnum } from 'src/models/status/enums';

import {
	removeTheAggregateIdsFromTheCollection,
	setTheStateForTheAggregateIds,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { createCompareFunction } from 'src/util/collection/customCompareFunction';
import { hideTheLoadingIndicator } from 'src/util/common';
import { QueryStore } from 'src/util/query/QueryStore';

import {
	GetAllGlobalFilteredItemsQueryCallBack,
	GetAllUsersWithAccessToTheModuleForTheUserFilterFilterListCallBack,
} from './callbacks';
import {
	CompleteListExportFetchPayload,
	DeleteSelectedAggregateIdsPayload,
	FetchFilterListPayload,
	QueryStorePayload,
	SetDateFilterPayload,
	SetDateTimeRangeFilterPayload,
	SetDirectionFilterPayload,
	SetFilterListPayload,
	SetFilteredBoxesPayload,
	SetFilteredModulesPayload,
	SetFilteredUsersPayload,
	SetListStatePayload,
	SetPaginationPagePayload,
	SetPaginationPayload,
	SetSelectedSharedQueryPayload,
	SetSortPayload,
} from './interfaces';

const DEFAULT_ROWS_PER_PAGE = 100;
const DEFAULT_LIST_SORT: GenericActivityListSort = {
	direction: 'DESC',
	property: 'timestamp',
};

const createDefaultPaginationEntry = (): GenericActivityListPagination => ({
	page: 1,
	rowsPerPage: DEFAULT_ROWS_PER_PAGE,
});

const createDefaultPaginationMap = (): TSMap<ActivityListTypeEnum, GenericActivityListPagination> =>
	new TSMap<ActivityListTypeEnum, GenericActivityListPagination>([
		[ActivityListTypeEnum.FILTER, createDefaultPaginationEntry()],
	]);

const replaceActiveQueryStoreSubscription = <T>({
	matchesLogicalView,
	nextSerializedQuery,
	parseQuery,
	queryStore,
	subscriptions,
}: {
	matchesLogicalView: (serializedQuery: SerializedQuery) => boolean;
	nextSerializedQuery: SerializedQuery;
	parseQuery: (serializedQuery: SerializedQuery) => T;
	queryStore: QueryStore | undefined;
	subscriptions: SerializedQuery[];
}): SerializedQuery[] => {
	const staleSubscriptions = subscriptions.filter(matchesLogicalView);

	staleSubscriptions.forEach((serializedQuery) => {
		void queryStore?.unsubscribeQuery(parseQuery(serializedQuery));
	});

	return [...subscriptions.filter((serializedQuery) => !matchesLogicalView(serializedQuery)), nextSerializedQuery];
};

export const useFilterListStore = defineStore('filterList', {
	actions: {
		addQueryStoreSubscription(serializedQuery: SerializedQuery): void {
			this.queryStoreSubscriptions.push(serializedQuery);
		},
		clearQueryStoreSubscriptions(): void {
			this.queryStoreSubscriptions = [];
		},
		clearSubscriptions(payload: QueryStorePayload): void {
			if (payload.queryStore !== undefined) {
				this.queryStoreSubscriptions.forEach((serializedQuery: SerializedQuery) => {
					switch (serializedQuery.type) {
						case 'GetAllGlobalFilteredItemsQuery':
							void payload.queryStore?.unsubscribeQuery(
								GetAllGlobalFilteredItemsQuery.fromJSON<GetAllGlobalFilteredItemsQuery>(
									serializedQuery.query
								)
							);
							break;
						default:
							break;
					}
				});
			}

			this.clearQueryStoreSubscriptions();
		},
		deleteSelectedAggregateIds(payload: DeleteSelectedAggregateIdsPayload): void {
			const filterListCopy = removeTheAggregateIdsFromTheCollection(
				this.filterList as never[],
				payload.aggregateIds
			) as unknown as FilterListItem[];

			if (filterListCopy !== this.filterList) {
				const filterListCopyPaginator = new Paginator(filterListCopy, DEFAULT_ROWS_PER_PAGE, 1);

				while (filterListCopyPaginator.current <= filterListCopyPaginator.total) {
					this.setFilterList({
						page: filterListCopyPaginator.current,
						payload: filterListCopyPaginator.page(filterListCopyPaginator.current) as FilterListItem[],
						totalCount: this.filterListTotalCount,
					});

					filterListCopyPaginator.next();
				}
			}
		},
		fetchFilterBoxes(): void {
			this.setFilterBoxes([]);
		},
		async fetchFilterList(payload: FetchFilterListPayload): Promise<void> {
			const hasFilterSelection = this.filteredModuleIds.length > 0 || this.filteredUsers.length > 0;

			if (payload.queryStore === undefined || !hasFilterSelection) {
				hideTheLoadingIndicator();
				this.setFilterList({
					page: 1,
					payload: [],
					totalCount: 0,
				});
				return;
			}

			const fetchToken = useGenericActivityListStore().beginListFetch(this.$id);
			this.registerUnsubscribeFetchFilterList(payload);

			const sort = this.sortFor(ActivityListTypeEnum.FILTER);
			const pagination = this.paginationFor(ActivityListTypeEnum.FILTER);
			const page = pagination.page;
			const query = new GetAllGlobalFilteredItemsQuery(
				payload.view,
				resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
				this.filteredBoxIds,
				this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
				this.filteredModuleIds,
				payload.searchTerm,
				this.directionFilter,
				pagination,
				sort
			);
			const queryIdentifier = query.uniqueQueryIdentifier();

			this.setPaginationPage({ activityListType: ActivityListTypeEnum.FILTER, page });
			this.setActiveQueryIdentifier(ActivityListTypeEnum.FILTER, queryIdentifier);

			const callback = new GetAllGlobalFilteredItemsQueryCallBack(this, page, queryIdentifier);
			callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
			await payload.queryStore.query<FilterListItem[]>({
				callback,
				createTime: new Date(),
				query,
				responseType: 'json',
				runType: FilterListItemRunType,
			});
		},
		fetchFilterModules(): void {
			const { t } = useI18n();

			this.setFilterModules(
				[
					{
						internalModuleIdentifier: 'phoneCallList',
						label: t('menu_phonecalls'),
						moduleIcon: 'far fa-phone',
						moduleIdentifier: 'module_phonecall',
					},
					{
						internalModuleIdentifier: 'taskList',
						label: t('menu_tasks'),
						moduleIcon: 'far fa-square-check',
						moduleIdentifier: 'module_task',
					},
					{
						internalModuleIdentifier: 'documentList',
						label: t('menu_documents'),
						moduleIcon: 'far fa-file-lines',
						moduleIdentifier: 'module_document',
					},
					{
						internalModuleIdentifier: 'mailList',
						label: t('menu_mails'),
						moduleIcon: 'far fa-envelope',
						moduleIdentifier: 'module_email',
					},
					{
						internalModuleIdentifier: 'noteList',
						label: t('menu_notes'),
						moduleIcon: 'far fa-pen-to-square',
						moduleIdentifier: 'module_note',
					},
					{
						internalModuleIdentifier: 'messageList',
						label: t('menu_messages'),
						moduleIcon: 'far fa-comment-alt',
						moduleIdentifier: 'module_message',
					},
					{
						internalModuleIdentifier: 'agendaList',
						label: t('menu_appointments'),
						moduleIcon: 'far fa-calendar-days',
						moduleIdentifier: 'module_appointment',
					},
				].sort((left, right) => {
					return left.label.trim().localeCompare(right.label.trim());
				})
			);
		},
		async fetchFilterUsers(payload: QueryStorePayload): Promise<void> {
			if (payload.queryStore === undefined) {
				this.setFilterUsers([]);
				return;
			}

			await payload.queryStore.query<Array<FilterUsers>>({
				callback: new GetAllUsersWithAccessToTheModuleForTheUserFilterFilterListCallBack(this),
				createTime: new Date(),
				query: new GetAllUsersWithAccessToTheModuleForTheUserFilterQuery(''),
				responseType: 'json',
				runType: FilterUsersRunType,
			});
		},
		async fetchTheCompleteListForExport(payload: CompleteListExportFetchPayload): Promise<FilterListItem[] | null> {
			const hasFilterSelection = this.filteredModuleIds.length > 0 || this.filteredUsers.length > 0;

			if (payload.queryStore === undefined || !hasFilterSelection) {
				return null;
			}

			const pagination: GenericActivityListPagination = {
				page: 1,
				rowsPerPage: this.filterListTotalCount + COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
			};
			const query = prepareQueryForCompleteListExport(
				new GetAllGlobalFilteredItemsQuery(
					payload.view,
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					this.filteredModuleIds,
					payload.searchTerm,
					this.directionFilter,
					pagination,
					this.sortFor(ActivityListTypeEnum.FILTER)
				)
			);

			let completeList: FilterListItem[] | null = null;

			await payload.queryStore.query<FilterListItem[]>({
				callback: new ResolveListForExportCallback<FilterListItem>((list) => {
					completeList = list;
				}),
				createTime: new Date(),
				query,
				responseType: 'json',
				runType: FilterListItemRunType,
			});

			return completeList;
		},
		isLatestQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): boolean {
			return this.activeQueryIdentifierByListType.get(activityListType) === queryIdentifier;
		},
		registerUnsubscribeFetchFilterList(payload: {
			queryStore: QueryStore | undefined;
			searchTerm: string;
			view: string;
		}): void {
			if (payload.queryStore === undefined) {
				return;
			}

			const pagination = this.paginationFor(ActivityListTypeEnum.FILTER);
			const query = new GetAllGlobalFilteredItemsQuery(
				payload.view,
				resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
				this.filteredBoxIds,
				this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
				this.filteredModuleIds,
				payload.searchTerm,
				this.directionFilter,
				pagination,
				this.sortFor(ActivityListTypeEnum.FILTER)
			);

			this.replaceFilterListQueryStoreSubscription({
				query,
				queryStore: payload.queryStore,
			});
		},
		replaceFilterListQueryStoreSubscription(payload: {
			query: GetAllGlobalFilteredItemsQuery;
			queryStore: QueryStore | undefined;
		}): void {
			const nextSerializedQuery: SerializedQuery = {
				query: JSON.stringify(payload.query),
				type: 'GetAllGlobalFilteredItemsQuery',
			};
			const expectedStandardFilterSelection = payload.query.standardFilterSelection;

			this.queryStoreSubscriptions = replaceActiveQueryStoreSubscription({
				matchesLogicalView: (serializedQuery) => {
					if (serializedQuery.type !== nextSerializedQuery.type) {
						return false;
					}

					return (
						GetAllGlobalFilteredItemsQuery.fromJSON<GetAllGlobalFilteredItemsQuery>(serializedQuery.query)
							.standardFilterSelection === expectedStandardFilterSelection
					);
				},
				nextSerializedQuery,
				parseQuery: (serializedQuery) =>
					GetAllGlobalFilteredItemsQuery.fromJSON<GetAllGlobalFilteredItemsQuery>(serializedQuery.query),
				queryStore: payload.queryStore,
				subscriptions: this.queryStoreSubscriptions,
			});
		},
		reset(payload: QueryStorePayload): void {
			this.clearSubscriptions({
				queryStore: payload.queryStore,
			});
			this.resetState();
		},
		resetState(): void {
			this.activeQueryIdentifierByListType = new TSMap<ActivityListTypeEnum, string>();
			this.dateTimeRangeFilter = PreconfiguredTimeRanges.TODAY;
			this.filterListTotalCount = 0;
			this.filterList = [];
			this.filterListSliced.clear();
			this.filterModules = [];
			this.filterUsers = [];
			this.filterBoxes = [];
			this.dateFilter = '';
			this.filteredUsers = [];
			this.filteredModuleIds = [];
			this.filteredBoxIds = [];
			this.directionFilter = 'combined';
			this.selectedSharedQueryId = '';
			this.queryStoreSubscriptions = [];
			this.pagination = createDefaultPaginationMap();
		},
		setActiveQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): void {
			this.activeQueryIdentifierByListType.set(activityListType, queryIdentifier);
		},
		setDateFilterAction(payload: SetDateFilterPayload): void {
			this.setDateFilterValue(payload.dateFilter);
		},
		setDateFilterValue(payload: DateRangeSelection | string): void {
			this.dateFilter = payload || '';
		},
		setDateTimeRangeFilter(payload: SetDateTimeRangeFilterPayload): void {
			this.setDateTimeRangeFilterValue(payload.dateTimeRangeFilter);
		},
		setDateTimeRangeFilterValue(payload: PreconfiguredTimeRanges): void {
			this.dateTimeRangeFilter = payload;
		},
		setDirectionFilterAction(payload: SetDirectionFilterPayload): void {
			this.setDirectionFilterValue(payload.directionFilter);
		},
		setDirectionFilterValue(payload: 'combined' | 'in' | 'out'): void {
			this.directionFilter = payload;
		},
		setFilterBoxes(payload: Array<FilterBoxes>): void {
			this.filterBoxes = payload || [];
		},
		setFilterList(payload: SetFilterListPayload): void {
			this.filterListTotalCount = payload.totalCount;
			this.filterListSliced.set(payload.page, payload.payload);

			const sort = this.sortFor(ActivityListTypeEnum.FILTER);
			this.filterList = dedupByAggregateId([...this.filterListSliced.values()].flatMap((entry) => entry)).sort(
				createCompareFunction<FilterListItem>(sort.property as keyof FilterListItem, sort.direction)
			);
		},
		setFilterModules(payload: Array<FilterModule>): void {
			this.filterModules = payload || [];
		},
		setFilterUsers(payload: Array<FilterUsers>): void {
			this.filterUsers = payload || [];
		},
		setFilteredBoxes(payload: Array<string>): void {
			this.filteredBoxIds = payload || [];
		},
		setFilteredBoxesAction(payload: SetFilteredBoxesPayload): void {
			this.setFilteredBoxes(payload.boxIdFilter);
		},
		setFilteredModules(payload: Array<string>): void {
			this.filteredModuleIds = payload || [];
		},
		setFilteredModulesAction(payload: SetFilteredModulesPayload): void {
			this.setFilteredModules(payload.moduleIdFilter);
		},
		setFilteredUsers(payload: Array<FilterUsers>): void {
			this.filteredUsers = payload || [];
		},
		setFilteredUsersAction(payload: SetFilteredUsersPayload): void {
			this.setFilteredUsers(payload.filterUsers);
		},
		setListLoadError(): void {
			const genericActivityListStore = useGenericActivityListStore();
			genericActivityListStore.setListLoadError();
		},
		setListViewModeLarge(payload: boolean): void {
			localStorage.setItem(FILTER_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE, JSON.stringify(payload));
			this.listViewModeLarge = payload;
		},
		setPagination(payload: SetPaginationPayload): void {
			this.pagination.set(payload.activityListType, payload.pagination);
		},
		setPaginationAction(payload: SetPaginationPayload): void {
			this.setPagination(payload);
		},
		setPaginationPage(payload: SetPaginationPagePayload): void {
			const pagination = this.paginationFor(payload.activityListType);
			pagination.page = payload.page;
			this.pagination.set(payload.activityListType, pagination);
		},
		setPaginationPageAction(payload: SetPaginationPagePayload): void {
			this.setPaginationPage(payload);
		},
		setSelectedSharedQuery(payload: string): void {
			this.selectedSharedQueryId = payload || '';
		},
		setSelectedSharedQueryAction(payload: SetSelectedSharedQueryPayload): void {
			this.setSelectedSharedQuery(payload.selectedSharedQuery ?? '');
		},
		setSort(payload: SetSortPayload): void {
			this.sort.set(payload.activityListType, payload.sort);
		},
		setSortAction(payload: SetSortPayload): void {
			this.setSort(payload);
			localStorage.setItem(FILTER_LIST_GENERIC_ACTIVITY_LIST_SORT, JSON.stringify(this.sort.toJSON()));
		},
		setTheFilterStateToReady(payload: SetListStatePayload): void {
			this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.READY);
		},
		setTheFilterStateToSeen(payload: SetListStatePayload): void {
			this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.SEEN);
		},
		setTheFilterStateToUnseen(payload: SetListStatePayload): void {
			this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.UNSEEN);
		},
		setTheStateForAggregateIds(aggregateIds: string[], status: StatusEnum): void {
			const filterListWithUpdatedState = setTheStateForTheAggregateIds(
				this.filterList as never[],
				aggregateIds,
				status
			) as unknown as FilterListItem[];

			if (filterListWithUpdatedState.length === 0) {
				return;
			}

			const filterListCopyPaginator = new Paginator(filterListWithUpdatedState, DEFAULT_ROWS_PER_PAGE, 1);

			while (filterListCopyPaginator.current <= filterListCopyPaginator.total) {
				this.setFilterList({
					page: filterListCopyPaginator.current,
					payload: filterListCopyPaginator.page(filterListCopyPaginator.current) as FilterListItem[],
					totalCount: this.filterListTotalCount,
				});

				filterListCopyPaginator.next();
			}
		},
		silentSetFilterList(): void {
			this.filterListTotalCount = 0;
			this.filterList = [];
			this.filterListSliced.clear();
			this.pagination.set(ActivityListTypeEnum.FILTER, createDefaultPaginationEntry());
		},
	},
	getters: {
		paginationFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListPagination {
			return (activityListType: ActivityListTypeEnum): GenericActivityListPagination =>
				state.pagination.get(activityListType) ?? createDefaultPaginationEntry();
		},
		sortFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListSort {
			return (activityListType: ActivityListTypeEnum): GenericActivityListSort =>
				state.sort.get(activityListType) ?? { ...DEFAULT_LIST_SORT };
		},
	},
	state: createFilterListState,
});
export * from './interfaces';
export * from './types';
